import { z } from 'zod';
import { id, paginatedResult, paginationQuery } from './common.js';

export const searchAlertFrequency = z.enum(['hourly', 'daily']);
export type SearchAlertFrequency = z.infer<typeof searchAlertFrequency>;
export const searchAlertAccess = z.object({
  canWrite: z.boolean(),
  inboxEnabled: z.boolean(),
  notice: z.string(),
});
export const searchAlertSubscription = z.object({
  searchId: id,
  name: z.string(),
  searchRevision: z.number().int().positive(),
  revision: z.number().int().nonnegative(),
  enabled: z.boolean(),
  frequency: searchAlertFrequency,
  canWrite: z.boolean(),
  summary: z.array(z.string()),
  applyUrl: z.string().startsWith('/find?'),
  searchDeleted: z.boolean(),
  staleDates: z.boolean(),
  lastCheckedAt: z.string().nullable(),
  nextCheckAt: z.string().nullable(),
  lastError: z.string().nullable(),
  lastMatchCount: z.number().int().nonnegative(),
  scanInProgress: z.boolean().default(false),
  lastCandidateCount: z.number().int().nonnegative().default(0),
});
export type SearchAlertSubscription = z.infer<typeof searchAlertSubscription>;
export const searchAlertSubscriptionPage = paginatedResult(searchAlertSubscription);
export const searchAlertSubscriptionQuery = paginationQuery
  .extend({
    status: z.enum(['all', 'enabled', 'paused']).default('all'),
    search: z.string().trim().max(100).optional(),
  })
  .strict();
export type SearchAlertSubscriptionQuery = z.infer<typeof searchAlertSubscriptionQuery>;
export const updateSearchAlertInput = z
  .object({
    enabled: z.boolean(),
    frequency: searchAlertFrequency,
    expectedRevision: z.number().int().nonnegative(),
    clientRequestId: z.string().uuid(),
  })
  .strict();
export type UpdateSearchAlertInput = z.infer<typeof updateSearchAlertInput>;
export const checkSearchAlertInput = z
  .object({
    expectedRevision: z.number().int().positive(),
    clientRequestId: z.string().uuid(),
  })
  .strict();
export type CheckSearchAlertInput = z.infer<typeof checkSearchAlertInput>;
export const searchAlertCheckResult = z.object({
  subscription: searchAlertSubscription,
  newMatchCount: z.number().int().nonnegative(),
  deliveredCount: z.number().int().nonnegative(),
  suppressedCount: z.number().int().nonnegative(),
  checkedAt: z.string(),
  replayed: z.boolean(),
});
export const searchAlertMatch = z.object({
  id,
  searchId: id,
  searchName: z.string(),
  shiftId: id,
  practiceName: z.string(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  timezone: z.string().nullable(),
  payRateCents: z.number().int().nullable(),
  currentAvailable: z.boolean(),
  unavailableReason: z.string().nullable(),
  matchedAt: z.string(),
  notificationStatus: z.enum(['delivered', 'suppressed']),
  shiftUrl: z.string().startsWith('/shifts/'),
  searchUrl: z.string().startsWith('/search-alerts?'),
});
export const searchAlertMatchPage = paginatedResult(searchAlertMatch);
export const searchAlertMatchQuery = paginationQuery
  .extend({ searchId: id.max(200).optional() })
  .strict();
export type SearchAlertMatchQuery = z.infer<typeof searchAlertMatchQuery>;
